import React, { useState, useEffect } from 'react';
import { STRRoom } from '../types';
import { formatNumber, parseNumberInput } from '../utils';
import AddressSearch from '../../AddressSearch';
import { useResponsive } from '../../hooks/useResponsive';

interface Props {
  room: STRRoom | null; // null = create mode
  onSave: (data: any) => void;
  onClose: () => void;
}

export default function RoomModal({ room, onSave, onClose }: Props) {
  const { isMobile } = useResponsive();
  const [showAddressSearch, setShowAddressSearch] = useState(false);

  const [form, setForm] = useState({
    name: '', address: '', road_addr: '', jibun_addr: '', zipcode: '', building_name: '', detail_address: '',
    room_type: '원룸', area_m2: '', floor: '', description: '',
    weekly_rent: '0', weekly_maintenance: '0', weekly_cleaning: '0', deposit: '330,000',
    monthly_rent: '0', monthly_maintenance: '0', monthly_cleaning: '0', monthly_deposit: '0',
    ownership_type: '', contract_start: '', contract_end: '', business_type: '', operation_start: '',
    setup_brokerage: '0', setup_interior: '0', setup_cleaning: '0',
    sublease_deposit: '0', sublease_rent: '0',
  });

  useEffect(() => {
    if (room) {
      setForm({
        name: room.name, address: room.address, road_addr: room.road_addr, jibun_addr: room.jibun_addr,
        zipcode: room.zipcode, building_name: room.building_name, detail_address: room.detail_address,
        room_type: room.room_type, area_m2: room.area_m2, floor: room.floor, description: room.description,
        weekly_rent: formatNumber(room.weekly_rent || 0), weekly_maintenance: formatNumber(room.weekly_maintenance || 0),
        weekly_cleaning: formatNumber(room.weekly_cleaning || 0), deposit: formatNumber(room.deposit || 330000),
        monthly_rent: formatNumber(room.monthly_rent || 0), monthly_maintenance: formatNumber(room.monthly_maintenance || 0),
        monthly_cleaning: formatNumber(room.monthly_cleaning || 0), monthly_deposit: formatNumber(room.monthly_deposit || 0),
        ownership_type: room.ownership_type, contract_start: room.contract_start, contract_end: room.contract_end,
        business_type: room.business_type, operation_start: room.operation_start,
        setup_brokerage: formatNumber(room.setup_brokerage || 0), setup_interior: formatNumber(room.setup_interior || 0),
        setup_cleaning: formatNumber(room.setup_cleaning || 0),
        sublease_deposit: formatNumber(room.sublease_deposit || 0), sublease_rent: formatNumber(room.sublease_rent || 0),
      });
    }
  }, [room]);

  const set = (key: string, val: string) => setForm((p) => ({ ...p, [key]: val }));

  const handleSelectAddress = (roadAddress: string, jibunAddress: string) => {
    setForm((p) => ({ ...p, road_addr: roadAddress, jibun_addr: jibunAddress, address: roadAddress }));
  };
  const setNumber = (key: string, val: string) => {
    const digits = val.replace(/[^0-9]/g, '');
    const num = parseInt(digits, 10) || 0;
    set(key, num === 0 ? '0' : formatNumber(num));
  };

  const handleSave = () => {
    if (!form.name.trim()) { alert('방 이름을 입력하세요'); return; }
    onSave({
      ...form,
      weekly_rent: parseNumberInput(form.weekly_rent),
      weekly_maintenance: parseNumberInput(form.weekly_maintenance),
      weekly_cleaning: parseNumberInput(form.weekly_cleaning),
      deposit: parseNumberInput(form.deposit),
      monthly_rent: parseNumberInput(form.monthly_rent),
      monthly_maintenance: parseNumberInput(form.monthly_maintenance),
      monthly_cleaning: parseNumberInput(form.monthly_cleaning),
      monthly_deposit: parseNumberInput(form.monthly_deposit),
      setup_brokerage: parseNumberInput(form.setup_brokerage),
      setup_interior: parseNumberInput(form.setup_interior),
      setup_cleaning: parseNumberInput(form.setup_cleaning),
      sublease_deposit: parseNumberInput(form.sublease_deposit),
      sublease_rent: parseNumberInput(form.sublease_rent),
    });
  };

  const wr = parseNumberInput(form.weekly_rent);
  const wm = parseNumberInput(form.weekly_maintenance);
  const dailyRent = Math.round(wr / 7);
  const dailyMaint = Math.round(wm / 7);

  const mr = parseNumberInput(form.monthly_rent);
  const mm = parseNumberInput(form.monthly_maintenance);
  const monthlyDailyRent = Math.round(mr / 30);
  const monthlyDailyMaint = Math.round(mm / 30);

  // c1 st-modal styles
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', border: '1px solid #e2e5e9',
    borderRadius: 8, fontSize: isMobile ? 16 : 14, boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: isMobile ? 12 : 13, fontWeight: 500, color: '#888', marginBottom: 4,
  };
  const groupStyle: React.CSSProperties = { marginBottom: isMobile ? 10 : 14 };
  const rowStyle: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12,
  };
  const sectionTitle: React.CSSProperties = {
    fontSize: isMobile ? 14 : 15, fontWeight: 600, margin: isMobile ? '16px 0 10px' : '20px 0 14px', padding: 0,
  };

  return (
    <>
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{ background: '#fff', borderRadius: isMobile ? 10 : 12, padding: isMobile ? 16 : 24, width: isMobile ? 'calc(100vw - 24px)' : '90%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: isMobile ? '0 0 14px' : '0 0 20px' }}>
          <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, margin: 0 }}>
            {room ? '방 수정' : '방 정보 입력'}
          </h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#aaa', padding: '4px', lineHeight: 1 }}
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        {/* 방 이름 */}
        <div style={groupStyle}>
          <label style={labelStyle}>방 이름 *</label>
          <input style={inputStyle} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="예: 강남역 오피스텔 201호" />
        </div>

        {/* 주소 */}
        <div style={groupStyle}>
          <label style={labelStyle}>도로명 주소</label>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1, backgroundColor: '#f8f9fa', cursor: 'pointer' }}
              value={form.road_addr}
              onClick={() => setShowAddressSearch(true)}
              readOnly
              placeholder="클릭하여 주소 검색"
            />
            <button
              type="button"
              onClick={() => setShowAddressSearch(true)}
              style={{
                padding: isMobile ? '12px 14px' : '10px 14px', border: '1px solid #1976d2', borderRadius: 8,
                background: '#1976d2', color: '#fff', fontSize: isMobile ? 14 : 13, fontWeight: 500,
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              주소 검색
            </button>
          </div>
        </div>

        {/* 건물명 / 상세주소 */}
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>건물명</label>
            <input style={inputStyle} value={form.building_name} onChange={(e) => set('building_name', e.target.value)} placeholder="예: OO빌딩" />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>상세 주소</label>
            <input style={inputStyle} value={form.detail_address} onChange={(e) => set('detail_address', e.target.value)} placeholder="101동 302호" />
          </div>
        </div>

        {/* 유형 / 면적 */}
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>유형</label>
            <select style={inputStyle} value={form.room_type} onChange={(e) => set('room_type', e.target.value)}>
              <option value="원룸">원룸</option>
              <option value="투룸">투룸</option>
              <option value="오피스텔">오피스텔</option>
              <option value="아파트">아파트</option>
              <option value="기타">기타</option>
            </select>
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>면적 (m²)</label>
            <input style={inputStyle} value={form.area_m2} onChange={(e) => set('area_m2', e.target.value)} placeholder="33" />
          </div>
        </div>

        {/* 층수 */}
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>층수</label>
            <select style={inputStyle} value={form.floor} onChange={(e) => set('floor', e.target.value)}>
              <option value="">선택</option>
              <option value="B2">지하2층</option>
              <option value="B1">지하1층</option>
              {Array.from({ length: 50 }, (_, i) => (
                <option key={i + 1} value={String(i + 1)}>{i + 1}층</option>
              ))}
            </select>
          </div>
          <div style={groupStyle} />
        </div>

        {/* 소유형태 / 사업자유형 */}
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>소유형태</label>
            <select style={inputStyle} value={form.ownership_type} onChange={(e) => set('ownership_type', e.target.value)}>
              <option value="">선택</option>
              <option value="소유">소유</option>
              <option value="전대 동의">전대 동의</option>
              <option value="임대차">임대차</option>
            </select>
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>사업자유형</label>
            <select style={inputStyle} value={form.business_type} onChange={(e) => set('business_type', e.target.value)}>
              <option value="">선택</option>
              <option value="개인">개인</option>
              <option value="임대사업자">임대사업자</option>
              <option value="법인">법인</option>
              <option value="숙박업등록">숙박업등록</option>
            </select>
          </div>
        </div>

        {/* 전대 보증금/월세 (전대 동의일 때만) */}
        {form.ownership_type === '전대 동의' && (
          <div style={rowStyle}>
            <div style={groupStyle}>
              <label style={labelStyle}>보증금</label>
              <input style={inputStyle} value={form.sublease_deposit} onChange={(e) => setNumber('sublease_deposit', e.target.value)} placeholder="0" />
            </div>
            <div style={groupStyle}>
              <label style={labelStyle}>월세</label>
              <input style={inputStyle} value={form.sublease_rent} onChange={(e) => setNumber('sublease_rent', e.target.value)} placeholder="0" />
            </div>
          </div>
        )}

        {/* 계약기간 */}
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>계약기간 시작일</label>
            <input type="date" style={inputStyle} value={form.contract_start} onChange={(e) => set('contract_start', e.target.value)} />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>계약기간 종료일</label>
            <input type="date" style={inputStyle} value={form.contract_end} onChange={(e) => set('contract_end', e.target.value)} />
          </div>
        </div>

        {/* 운영시작일 */}
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>운영시작일</label>
            <input type="date" style={inputStyle} value={form.operation_start} onChange={(e) => set('operation_start', e.target.value)} />
          </div>
          <div style={groupStyle} />
        </div>

        {/* 초기 세팅비 섹션 */}
        <h3 style={sectionTitle}>초기 세팅비</h3>
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>중개보수</label>
            <input style={inputStyle} value={form.setup_brokerage} onChange={(e) => setNumber('setup_brokerage', e.target.value)} placeholder="0" />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>내부인테리어</label>
            <input style={inputStyle} value={form.setup_interior} onChange={(e) => setNumber('setup_interior', e.target.value)} placeholder="0" />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>입주청소</label>
            <input style={inputStyle} value={form.setup_cleaning} onChange={(e) => setNumber('setup_cleaning', e.target.value)} placeholder="0" />
          </div>
          <div style={groupStyle} />
        </div>

        {/* 주간 요금 설정 섹션 */}
        <h3 style={sectionTitle}>주간(7일) 요금 설정</h3>
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>임대료 (원/주)</label>
            <input style={inputStyle} value={form.weekly_rent} onChange={(e) => setNumber('weekly_rent', e.target.value)} placeholder="350,000" />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>관리비 (원/주)</label>
            <input style={inputStyle} value={form.weekly_maintenance} onChange={(e) => setNumber('weekly_maintenance', e.target.value)} placeholder="50,000" />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>청소비 (원/1회)</label>
            <input style={inputStyle} value={form.weekly_cleaning} onChange={(e) => setNumber('weekly_cleaning', e.target.value)} placeholder="30,000" />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>보증금 (원)</label>
            <input style={inputStyle} value={form.deposit} onChange={(e) => setNumber('deposit', e.target.value)} placeholder="330,000" />
          </div>
        </div>
        {(dailyRent > 0 || dailyMaint > 0) && (
          <div style={{ fontSize: 12, color: '#888', marginTop: -8, marginBottom: 8 }}>
            일 {formatNumber(dailyRent + dailyMaint)}원 = 임대료 일 {formatNumber(dailyRent)}원 / 관리비 일 {formatNumber(dailyMaint)}원
          </div>
        )}

        {/* 월간 요금 설정 섹션 */}
        <h3 style={sectionTitle}>월간 요금 설정</h3>
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>임대료 (원/월)</label>
            <input style={inputStyle} value={form.monthly_rent} onChange={(e) => setNumber('monthly_rent', e.target.value)} placeholder="0" />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>관리비 (원/월)</label>
            <input style={inputStyle} value={form.monthly_maintenance} onChange={(e) => setNumber('monthly_maintenance', e.target.value)} placeholder="0" />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={groupStyle}>
            <label style={labelStyle}>청소비 (원/1회)</label>
            <input style={inputStyle} value={form.monthly_cleaning} onChange={(e) => setNumber('monthly_cleaning', e.target.value)} placeholder="0" />
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>보증금 (원)</label>
            <input style={inputStyle} value={form.monthly_deposit} onChange={(e) => setNumber('monthly_deposit', e.target.value)} placeholder="0" />
          </div>
        </div>
        {(monthlyDailyRent > 0 || monthlyDailyMaint > 0) && (
          <div style={{ fontSize: 12, color: '#888', marginTop: -8, marginBottom: 4 }}>
            일 {formatNumber(monthlyDailyRent + monthlyDailyMaint)}원 = 임대료 일 {formatNumber(monthlyDailyRent)}원 / 관리비 일 {formatNumber(monthlyDailyMaint)}원
          </div>
        )}
        {mr > 0 && wr > 0 && mr !== wr && (
          <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 8 }}>
            주간·월간 요금이 모두 입력되어 있습니다. 대표 요금은 주간 요금 기준입니다.
          </div>
        )}

        {/* 버튼 (c1: st-form-actions) */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: isMobile ? 10 : 8, marginTop: 20 }}>
          {isMobile ? (
            <>
              <button
                onClick={handleSave}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '100%', padding: '12px', border: 'none', borderRadius: 8,
                  background: '#1976d2', color: '#fff', fontSize: 14, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                저장
              </button>
              <button
                onClick={onClose}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '100%', padding: '12px', border: '1px solid #e2e5e9', borderRadius: 8,
                  background: '#f5f5f5', color: '#333', fontSize: 14, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '8px 16px', border: '1px solid #e2e5e9', borderRadius: 8,
                  background: '#f5f5f5', color: '#333', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleSave}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '8px 16px', border: 'none', borderRadius: 8,
                  background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                저장
              </button>
            </>
          )}
        </div>
      </div>
    </div>

    {showAddressSearch && (
      <AddressSearch
        onClose={() => setShowAddressSearch(false)}
        onSelectAddress={handleSelectAddress}
      />
    )}
    </>
  );
}
